<template>
  <div v-if="dialogVisible" class="subscription-overlay">
    <div class="subscription-dialog">
      <button class="subscription-close" type="button" @click="closeDialog">
        ✕
      </button>

      <section class="subscription-content">
        <div v-if="showActivityBanner" class="activity-banner">
          <p v-if="activityBannerSubTitle" class="activity-banner__subtitle">
            {{ activityBannerSubTitle }}
          </p>
          <p v-if="activityBannerMainTitle" class="activity-banner__title">
            {{ activityBannerMainTitle }}
          </p>
          <p v-if="activityBannerSubTitle" class="activity-banner__subtitle">
            {{ activityEndDateText }}
          </p>
        </div>

        <div class="plan-header">
          <div class="plan-title-row">
            <h1>{{ planMainTitle }}</h1>
            <button
              type="button"
              class="faq-help-btn"
              aria-label="常见问题"
              @click="scrollToFaq"
            >
              <span class="faq-help-icon">?</span>
              <span class="faq-help-tooltip">常见问题</span>
            </button>
          </div>
        </div>
        <!-- 企业已订阅管理员：增席说明与入口放在标题下方，避免挤占居中标题 -->
        <div v-if="canShowAddSeats" class="add-seats-banner">
          <p class="add-seats-banner__copy">
            {{ $t("header.memberCenter.addSeatsHint") }}
          </p>
          <button
            type="button"
            class="add-seats-banner__btn"
            @click="openAddSeatsFromSubscription"
          >
            {{ $t("header.memberCenter.addSeats") }}
          </button>
        </div>

        <div
          v-if="!plansLoading && availableBillingCycles.length > 0"
          class="cycle-tabs"
        >
          <button
            v-for="cycle in availableBillingCycles"
            :key="cycle.id"
            type="button"
            :class="{ active: activeCycle === cycle.id }"
            @click="selectCycle(cycle.id)"
          >
            {{ cycle.label }}
          </button>
        </div>

        <div
          v-if="plansLoading"
          class="plan-loading"
          v-loading="true"
          element-loading-background="rgba(0, 0, 0, 0.8)"
        ></div>
        <div v-else-if="visiblePlans.length === 0" class="plan-empty">
          暂无可用套餐
        </div>
        <div
          v-else
          class="plan-grid"
          :style="{ gridTemplateColumns: planGridColumns }"
        >
          <article
            v-for="plan in visiblePlans"
            :key="plan.id"
            class="plan-card"
            :class="{
              active: selectedPlanId === plan.id && !isPlanDisabled(plan),
              recommended: isRecommended(plan),
              disabled: isPlanDisabled(plan),
            }"
            @click="selectPlan(plan)"
          >
            <div v-if="isRecommended(plan)" class="recommend-badge">推荐</div>
            <h2>{{ plan.name }}</h2>

            <div class="price-row">
              <span class="price"
                >¥{{ formatPriceFromFen(plan.sellingPrice) }}</span
              >
              <span class="cycle-unit"
                >/{{ cycleUnit }}{{ isEnterprise ? "/席位" : "" }}</span
              >
             
            </div>
						<div
                v-if="plan.originalPrice > plan.sellingPrice"
                class="original-price"
              >
                ¥{{ formatPriceFromFen(plan.originalPrice) }}
								<span
                >/{{ cycleUnit }}{{ isEnterprise ? "/席位" : "" }}</span
              >
              </div>
            <div class="price-meta">
              {{ formatMonthlyPrice(plan.sellingPrice) }}元/月{{
                isEnterprise ? "/席位" : ""
              }}<span v-if="!isEnterprise">
                · 1积分={{ formatPlanIntegralUnitPrice(plan) }}元</span
              >
            </div>
            <div class="divider"></div>

            <div class="points-row">
              <RollingNumber
                class="points-value"
                :value="plan.monthlyIntegral"
                :replay-key="activeCycle"
              />
              <span style="margin-left: 0.28571rem"
                >积分/月{{ isEnterprise ? "/席位" : "" }}</span
              >
            </div>

            <div class="divider"></div>
            <div v-if="isEnterprise" class="enterprise-seat-block">
              <div class="seat-row">
                <div class="seat-stepper">
                  <button
                    type="button"
                    class="seat-stepper__btn"
                    :disabled="getPlanSeat(plan) <= minPlanSeat"
                    @click.stop="changePlanSeat(plan, -1)"
                  >
                    −
                  </button>
                  <input
                    class="seat-stepper__value"
                    :value="getPlanSeat(plan)"
                    type="text"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    @click.stop
                    @input.stop="handlePlanSeatInput(plan, $event)"
                    @blur="handlePlanSeatBlur(plan, $event)"
                    @keydown.enter.stop.prevent="$event.target.blur()"
                  />
                  <button
                    type="button"
                    class="seat-stepper__btn"
                    :disabled="getPlanSeat(plan) >= maxPlanSeat"
                    @click.stop="changePlanSeat(plan, 1)"
                  >
                    +
                  </button>
                </div>
                <span class="seat-label">席位</span>
              </div>
              <div class="seat-total-row">
                <span class="seat-total-label">合计</span>
                <span class="seat-total-price"
                  >¥{{ formatPriceFromFen(getPlanTotalPrice(plan)) }}</span
                >
                <span class="seat-total-unit">/{{ cycleUnit }}</span>
              </div>
              <div class="team-monthly-integral">
                团队内每月共<span class="team-monthly-integral-value">{{
                  formatNumber(getPlanTeamMonthlyIntegral(plan))
                }}</span
                >积分
              </div>
              <div class="divider"></div>
            </div>

            <ul class="benefit-list">
              <li
                class="vip"
                v-if="plan.activityMainTitle || plan.activitySubtitle"
              >
                VIP专享
              </li>
              <li v-if="plan.activityMainTitle" class="discount">
                {{ plan.activityMainTitle }}
              </li>
              <li v-if="plan.activitySubtitle" class="video">
                {{ plan.activitySubtitle }}
              </li>
            </ul>

            <button
              type="button"
              class="open-button"
              :class="{
                'active-plan-button': isPlanActiveButton(plan),
                'upgrade-plan-button': isPlanUpgradeButton(plan),
                'contact-admin-plan-button': isPlanContactAdminButton(plan),
              }"
              :disabled="isPlanDisabled(plan)"
              @click.stop="handleSubscribe(plan)"
            >
              {{ getPlanButtonText(plan) }}
            </button>
          </article>
        </div>

        <section
          v-if="!plansLoading && visiblePlans.length > 0"
          class="benefits-table"
        >
          <h3>权益对比</h3>
          <div class="table-wrap">
            <div
              class="table-row table-head"
              :style="{ gridTemplateColumns: tableGridColumns }"
            >
              <div>权益</div>
              <div v-for="plan in visiblePlans" :key="plan.id">
                {{ plan.name }}
              </div>
            </div>
            <div
              v-for="benefit in benefitRows"
              :key="benefit.key"
              class="table-row"
              :style="{ gridTemplateColumns: tableGridColumns }"
            >
              <div>{{ benefit.label }}</div>
              <div v-for="plan in visiblePlans" :key="plan.id">
                {{ getBenefitValue(plan, benefit.key) }}
              </div>
            </div>
          </div>
        </section>

        <section id="subscription-faq" ref="faqSection" class="faq-section">
          <h3>常见问题</h3>
          <div class="faq-list">
            <div
              v-for="item in faqItems"
              :key="item.id"
              class="faq-item"
              :class="{ expanded: isFaqExpanded(item.id) }"
            >
              <button
                type="button"
                class="faq-question"
                @click="toggleFaq(item.id)"
              >
                <span>{{ item.question }}</span>
                <i
                  :class="
                    isFaqExpanded(item.id)
                      ? 'el-icon-arrow-up'
                      : 'el-icon-arrow-down'
                  "
                ></i>
              </button>
              <div v-show="isFaqExpanded(item.id)" class="faq-answer">
                <p v-for="(paragraph, index) in item.paragraphs" :key="index">
                  {{ paragraph }}
                </p>
              </div>
            </div>
          </div>
        </section>
      </section>
    </div>

    <subscription-pay-dialog
      :visible.sync="payDialogVisible"
      :plan="payPlan"
      :cycle="activeCycle"
      @success="onPaySuccess"
      @close="onPayClose"
    />

    <subscription-upgrade-pay-dialog
      :visible.sync="upgradeDialogVisible"
      :plan="upgradePlan"
      :cycle="activeCycle"
      @success="onUpgradeSuccess"
      @close="onUpgradeClose"
    />
  </div>
</template>

<script>
import RollingNumber from "@/components/RollingNumber.vue";
import SubscriptionPayDialog from "@/views/subscription/SubscriptionPayDialog.vue";
import SubscriptionUpgradePayDialog from "@/views/subscription/SubscriptionUpgradePayDialog.vue";
import {
  PLAN_BUTTON_ACTION,
  MAX_SUBSCRIPTION_SEAT,
  accountTypeToClientType,
  cycleToPeriodType,
  fenToYuan,
  formatActiveSubscriptionButtonText,
  formatIntegralUnitPriceYuan,
  formatPlanButtonText,
  formatPrice,
  formatPriceFromFen,
  getActivityEndDateDisplay,
  getPlanButtonTextByAction,
  isPlanButtonActionEnabled,
  normalizeSubscriptionSeat,
  resolvePlanButtonAction,
  isSubscriptionButtonHidden,
  syncSubscriptionAccountTypeFromUser,
} from "@/utils/subscription";
import { isSuperAdmin } from "@/utils/accountRole";

export default {
  name: "SubscriptionPanel",
  components: {
    RollingNumber,
    SubscriptionPayDialog,
    SubscriptionUpgradePayDialog,
  },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      activeCycle: "year",
      selectedPlanId: "",
      // 周期 Tab 顺序：年卡 → 半年卡 → 季卡 → 月卡；periodType 4 对应半年卡
      billingCycles: [
        { id: "year", label: "年卡", unit: "年" },
        { id: "halfYear", label: "半年卡", unit: "半年" },
        { id: "season", label: "季卡", unit: "季" },
        { id: "month", label: "月卡", unit: "月" },
      ],
      benefitRows: [
        { key: "points", label: "每月积分" },
        { key: "pointPrice", label: "1积分 =" },
        { key: "discount", label: "模型专享折扣" },
      ],
      expandedFaqIds: [],
      faqItems: [
        {
          id: "points-diff",
          question: "订阅积分和充值积分有什么区别?",
          paragraphs: [
            "订阅积分来自购买订阅套餐每月赠送，到期日清零。充值积分来自直接充值，永久有效。两种积分消耗价格一致，系统优先消耗订阅积分。",
          ],
        },
        {
          id: "upgrade",
          question: "如何升档?",
          paragraphs: [
            "补差价 = 新套餐价格 − 旧套餐剩余月份价值。剩余月份 = 未发放积分的月份数（当月已发放 = 已消费，不计入抵扣）。剩余多少个月，即按旧套餐月均价抵扣相应金额。",
            "升档不限周期和档位（如月卡升级年卡、标准版升级豪华版均可），升级后当天生效，到期日按新周期重新计算。原有剩余积分合并至新周期，清零规则同步按新周期执行。",
          ],
        },
        {
          id: "expire",
          question: "今天我买了，什么时候到期？积分怎么给？",
          paragraphs: [
            "按「满对月」计算：到期日 = 购买日后推 N 个月的同一天。推不到同一天就取当月最后一天。",
            "积分发放：购买当天到账当月积分。之后每个对月日自动清零旧积分，同时发放新一期。共发 N 次，到期日清完最后一次。",
            "例：6月27日买年卡，当天到账当月积分。7月27日清零旧分 + 发新一期，8月27日重复......12个月后到期，清完即止。月卡只发1次、到期清零。",
          ],
        },
        {
          id: "downgrade",
          question: "可以降档吗？",
          paragraphs: [
            "不支持任何形式的降档，包括降档位（如豪华版降为标准版）和降周期（如年卡降为月卡）。您只能选择同档续费或向上升档。",
          ],
        },
        {
          id: "refund",
          question: "订阅后可以退款吗？",
          paragraphs: [
            "订阅购买后不支持退款，不可退换。中途弃用不会按比例退费，降档也不支持。当前周期到期后自动失效。建议先买月卡体验，满意后再升档享受更大折扣。",
            "补充说明：订阅属于虚拟数字服务，根据《消费者权益保护法》相关规定，在线交付的数字商品不支持七天无理由退货。",
          ],
        },
        {
          id: "no-renew",
          question: "到期不续费会怎样？会自动扣钱吗？",
          paragraphs: [
            "到期当天 23:59:59 订阅权益和剩余订阅积分自动失效，充值积分不受影响。到期后需重新购买，不会自动扣款。账号仍可正常登录使用基础功能。",
          ],
        },
        {
          id: "price-change",
          question: "套餐价格以后会变吗？影响我吗？",
          paragraphs: [
            "后台调整套餐价格和折扣不会影响已订阅用户。您按购买时的规则执行，到期续费时才按新规则走。早买早锁定当前优惠。",
          ],
        },
        {
          id: "max-plans",
          question: "最多能订阅几个套餐？",
          paragraphs: [
            "每个用户当前只可持有 1 个有效订阅（个人订阅或所在企业的企业订阅）。如需更换套餐请先升档或到期后续费切换。",
          ],
        },
        {
          id: "points-clear",
          question: "订阅积分什么时候清零？",
          paragraphs: [
            "每月积分独立核算，当月积分在发出后满一个月自动清零，不是自然月月底。",
            "例如：6 月 26 日购买，积分当天到账 -> 7 月 26 日 23:59:59 清零；同日发放下一笔积分，开始新周期。月月如此，用不完不跨月。",
          ],
        },
        {
          id: "enterprise-member",
          question: "企业成员能订阅吗？",
          paragraphs: ["企业内仅管理员可操作订阅，普通成员不能订阅。"],
        },
        {
          id: "payment-arrival",
          question: "支付后多久到账？",
          paragraphs: [
            "支付成功后订阅即时生效，积分立即到账。如遇延迟请等待 5 分钟刷新，仍未到账请联系客服。",
          ],
        },
        {
          id: "what-seat",
          question: "什么是席位？",
          paragraphs: [
            "席位就是团队的成员名额。购买团队版套餐时，默认包含 1 个所有者席位（购买者本人）。管理员和普通成员需额外购买席位后邀请加入，每个成员占用 1 个席位，消耗的积分按管理员设置的配额上限从团队扣减。",
          ],
        },
        {
          id: "increase-seats",
          question: "如何增加席位？",
          paragraphs: [
            "在团队管理的成员页面中点击「增加席位」。增席费用按当前套餐单价 × 剩余月数折算。增加的积分按月均分到后续月份发放，当月已发部分不计入补收。",
          ],
        },
        {
          id: "price-seats",
          question: "席位价格是怎么算的？",
          paragraphs: [
            "每个席位的价格 = 团队套餐单价。购买 N 个席位，总价 = 套餐单价 × N，积分跟着席位同比例增长，全部进入企业超级管理员的订阅积分账户。",
          ],
        },
        {
          id: "team-points",
          question: "团队积分配额和个人积分有什么区别？",
          paragraphs: [
            "企业订阅积分进入超级管理员账户，再由管理员从本人账户向后代账号分配。各账号使用本人的直充积分和订阅积分。个人版积分归个人所有。",
          ],
        },
        {
          id: "zeroing-out",
          question: "增席后积分怎么算？什么时候清零？",
          paragraphs: [
            "增席不延长会员有效期，只新增席位和对应积分。",
            "非末周期增席：当月积分滚入下个周期，跟下期积分一起清零。",
            "末周期增席：积分自获取日 +1 个月后清零（非会员到期日）。",
            "月卡增席：积分获取日 +1 个月清零，与会籍到期无关。",
          ],
        },
      ],
      payDialogVisible: false,
      payPlan: null,
      upgradeDialogVisible: false,
      upgradePlan: null,
    };
  },
  computed: {
    isEnterprise() {
      return this.accountType === "enterprise";
    },
    // 订阅入口被后台隐藏时，增席入口同步隐藏
    subscriptionButtonHidden() {
      return isSubscriptionButtonHidden();
    },
    // 新增席位：企业仅超级管理员可操作
    isAdmin() {
      try {
        const user = JSON.parse(window.localStorage.getItem("user") || "{}");
        return isSuperAdmin(user);
      } catch (error) {
        return isSuperAdmin();
      }
    },
    // 与个人中心 hasSubscription 一致：未隐藏订阅入口且当前已开通套餐
    hasSubscription() {
      return (
        !this.subscriptionButtonHidden &&
        Boolean(this.userSubscription.subscribed)
      );
    },
    // 套餐页「新增席位」可见性与个人中心按钮保持同一套条件
    canShowAddSeats() {
      return this.isEnterprise && this.hasSubscription && this.isAdmin;
    },
    dialogVisible: {
      get() {
        return this.visible;
      },
      set(value) {
        this.$emit("update:visible", value);
      },
    },
    accountType() {
      return this.normalizeAccountType(
        this.$store.state.subscriptionAccountType,
      );
    },
    activeSubscriptionPlan() {
      return this.$store.state.activeSubscriptionPlan || {};
    },
    plansLoading() {
      return this.$store.state.subscriptionPlansLoading;
    },
    planList() {
      return this.$store.state.subscriptionPlans || [];
    },
    availableBillingCycles() {
      return this.billingCycles.filter((cycle) =>
        this.planList.some(
          (plan) => Number(plan.periodType) === cycleToPeriodType(cycle.id),
        ),
      );
    },
    userSubscription() {
      return this.$store.state.userSubscription || {};
    },
    subscriptionPlanMeta() {
      return this.$store.state.subscriptionPlanMeta || {};
    },
    activityBannerName() {
      return this.subscriptionPlanMeta.activityName || "";
    },
    activityBannerMainTitle() {
      return this.subscriptionPlanMeta.mainTitle || "";
    },
    activityBannerSubTitle() {
      return this.subscriptionPlanMeta.subTitle || "";
    },
    activityEndDateText() {
      return getActivityEndDateDisplay(this.subscriptionPlanMeta.endDate);
    },
    showActivityBanner() {
      return Boolean(
        this.activityBannerName ||
          this.activityBannerMainTitle ||
          this.activityBannerSubTitle,
      );
    },
    planMainTitle() {
      return "选择适合你的订阅方案";
    },
    planSubTitle() {
      return "年卡折扣最大，所有方案均可随时升档";
    },
    planGridColumns() {
      const count = Math.min(Math.max(this.visiblePlans.length, 1), 5);
      return `repeat(${count}, 1fr)`;
    },
    tableGridColumns() {
      const count = Math.max(this.visiblePlans.length, 1);
      return `1.2fr repeat(${count}, 1fr)`;
    },
    cycleUnit() {
      const cycle = this.billingCycles.find(
        (item) => item.id === this.activeCycle,
      );
      return cycle ? cycle.unit : "年";
    },
    visiblePlans() {
      const periodType = cycleToPeriodType(this.activeCycle);
      return this.planList
        .filter((plan) => Number(plan.periodType) === periodType)
        .sort((a, b) => Number(a.sortOrder || 0) - Number(b.sortOrder || 0));
    },
    activePlanCombo() {
      const plan = this.activeSubscriptionPlan;
      if (!plan.planId || !Number(plan.level)) {
        return "";
      }
      const cycle = plan.cycle || this.activeCycle;
      return `${cycle}-${plan.planId}`;
    },
    minPlanSeat() {
      return normalizeSubscriptionSeat(this.userSubscription.seats);
    },
    maxPlanSeat() {
      return MAX_SUBSCRIPTION_SEAT;
    },
  },
  watch: {
    availableBillingCycles: {
      immediate: true,
      handler(cycles) {
        if (
          cycles.length > 0 &&
          !cycles.some((cycle) => cycle.id === this.activeCycle)
        ) {
          this.activeCycle = cycles[0].id;
        }
      },
    },
    visible(value) {
      if (value) {
        syncSubscriptionAccountTypeFromUser(this.$store);
        this.loadPlans().then(() => {
          this.selectDefaultPlan();
        });
      }
    },
    activeCycle() {
      this.ensureSelectablePlan();
    },
    minPlanSeat() {
      this.ensurePlanSeats();
    },
    accountType() {
      this.loadPlans().then(() => {
        this.ensureSelectablePlan();
      });
    },
  },
  mounted() {
    syncSubscriptionAccountTypeFromUser(this.$store);
    if (this.visible) {
      this.loadPlans().then(() => {
        this.selectDefaultPlan();
      });
    }
  },
  methods: {
    loadPlans() {
      const clientType = accountTypeToClientType(this.accountType);
      return Promise.all([
        this.$store.dispatch("fetchSubscriptionPlans", clientType),
        this.$store.dispatch("fetchUserSubscription"),
      ])
        .then((result) => {
          this.ensurePlanSeats();
          return result;
        })
        .catch((error) => {
          if (this.$message) {
            this.$message.error(error.message || "获取套餐失败");
          }
          return [];
        });
    },
    closeDialog() {
      this.dialogVisible = false;
      this.$emit("close");
    },
    // 关闭套餐弹窗，由顶栏打开个人中心并自动拉起新增席位流程
    openAddSeatsFromSubscription() {
      this.closeDialog();
      this.$emit("open-add-seats");
    },
    normalizeAccountType(accountType) {
      return accountType === "team" || accountType === "enterprise"
        ? "enterprise"
        : "personal";
    },

    applyPlanCombo(comboValue) {
      if (!comboValue) {
        this.$store.commit("setActiveSubscriptionPlan", {
          accountType: "",
          planId: "",
          level: 0,
          cycle: "",
          planName: "",
        });
        this.selectDefaultPlan();
        return;
      }

      const separatorIndex = comboValue.indexOf("-");
      if (separatorIndex < 0) {
        return;
      }
      const cycle = comboValue.slice(0, separatorIndex);
      const planId = comboValue.slice(separatorIndex + 1);
      const basePlan = this.planList.find((item) => item.id === planId);
      if (!cycle || !basePlan) {
        return;
      }

      this.activeCycle = cycle;
      this.selectedPlanId = planId;
      this.$store.commit("setActiveSubscriptionPlan", {
        accountType: this.accountType,
        cycle,
        planId,
        configId: basePlan.configId,
        level: basePlan.level,
        planName: basePlan.name,
      });
      this.ensureSelectablePlan();
    },
    selectCycle(cycle) {
      this.activeCycle = cycle;
    },
    hasPlanCycle(plan, cycle) {
      if (!plan) {
        return false;
      }
      return (
        plan.configId != null &&
        Number(plan.periodType) === cycleToPeriodType(cycle)
      );
    },
    selectPlan(plan) {
      if (this.isPlanDisabled(plan)) {
        return;
      }
      this.normalizePlanSeat(plan);
      this.selectedPlanId = plan.id;
    },
    handleSubscribe(plan) {
      if (this.isPlanDisabled(plan)) {
        return;
      }
      this.normalizePlanSeat(plan);
      this.selectedPlanId = plan.id;
      const seat = this.getPlanSeat(plan);
      const payload = {
        accountType: this.accountType,
        clientType: accountTypeToClientType(this.accountType),
        cycle: this.activeCycle,
        planId: plan.id,
        configId: plan.configId,
        level: plan.level,
        planName: plan.name,
        seat,
      };
      this.$store.commit("setActiveSubscriptionPlan", payload);
      const action = this.getPlanButtonAction(plan);
      if (action === PLAN_BUTTON_ACTION.UPGRADE) {
        this.upgradePlan = plan;
        this.upgradeDialogVisible = true;
      } else {
        this.payPlan = plan;
        this.payDialogVisible = true;
      }
      this.$emit("subscribe", payload);
    },
    onPaySuccess(payload) {
      this.$store.commit("setActiveSubscriptionPlan", {
        accountType: this.accountType,
        cycle: payload.cycle,
        planId: payload.plan.id,
        configId: payload.plan.configId,
        level: payload.plan.level,
        planName: payload.plan.name,
      });
      this.payPlan = null;
      this.loadPlans().then(() => {
        this.ensureSelectablePlan();
      });
    },
    onPayClose() {
      this.payPlan = null;
    },
    onUpgradeSuccess(payload) {
      this.onPaySuccess(payload);
      this.upgradePlan = null;
    },
    onUpgradeClose() {
      this.upgradePlan = null;
    },
    getSelectableRecommendedPlan() {
      return this.visiblePlans.find(
        (plan) => this.isRecommended(plan) && !this.isPlanDisabled(plan),
      );
    },
    selectDefaultPlan() {
      const recommended = this.getSelectableRecommendedPlan();
      if (recommended) {
        this.selectedPlanId = recommended.id;
        return;
      }
      this.ensureSelectablePlan();
    },
    ensureSelectablePlan() {
      const selected = this.visiblePlans.find(
        (plan) => plan.id === this.selectedPlanId,
      );
      if (selected && !this.isPlanDisabled(selected)) {
        return;
      }
      const recommended = this.getSelectableRecommendedPlan();
      const nextPlan =
        recommended ||
        this.visiblePlans.find((plan) => !this.isPlanDisabled(plan));
      this.selectedPlanId = nextPlan ? nextPlan.id : "";
    },
    isRecommended(plan) {
      return Boolean(plan.recommended);
    },
    isPlanDisabled(plan) {
      return !isPlanButtonActionEnabled(this.getPlanButtonAction(plan));
    },
    getPlanButtonAction(plan) {
      return resolvePlanButtonAction({
        upgradeStatus: plan.upgradeStatus,
      });
    },
    getPlanButtonText(plan) {
      const action = this.getPlanButtonAction(plan);
      if (action === PLAN_BUTTON_ACTION.ACTIVE) {
        if (
          this.userSubscription.subscribed &&
          this.userSubscription.planName
        ) {
          return formatActiveSubscriptionButtonText(this.userSubscription);
        }
        return formatPlanButtonText(plan, this.activeCycle);
      }
      return getPlanButtonTextByAction(action);
    },
    isPlanActiveButton(plan) {
      return this.getPlanButtonAction(plan) === PLAN_BUTTON_ACTION.ACTIVE;
    },
    isPlanUpgradeButton(plan) {
      return this.getPlanButtonAction(plan) === PLAN_BUTTON_ACTION.UPGRADE;
    },
    isPlanContactAdminButton(plan) {
      return (
        this.getPlanButtonAction(plan) === PLAN_BUTTON_ACTION.CONTACT_ADMIN
      );
    },
    getBenefitValue(plan, key) {
      if (key === "points") {
        return `${this.formatNumber(plan.monthlyIntegral)}分`;
      }
      if (key === "pointPrice") {
        return `${this.formatPlanIntegralUnitPrice(plan)}元`;
      }
      if (key === "discount") {
        return plan.discount;
      }
      return "-";
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString("zh-CN");
    },
    getPlanSeat(plan) {
      return Math.max(
        this.minPlanSeat,
        normalizeSubscriptionSeat(plan && plan.seat),
      );
    },
    normalizePlanSeat(plan) {
      if (!plan) {
        return this.minPlanSeat;
      }
      const seat = this.getPlanSeat(plan);
      if (Number(plan.seat) !== seat) {
        this.$set(plan, "seat", seat);
      }
      return seat;
    },
    ensurePlanSeats() {
      this.planList.forEach((plan) => {
        this.normalizePlanSeat(plan);
      });
    },
    changePlanSeat(plan, delta) {
      if (!plan) {
        return;
      }
      const nextSeat = Math.min(
        this.maxPlanSeat,
        Math.max(this.minPlanSeat, this.getPlanSeat(plan) + Number(delta || 0)),
      );
      this.$set(plan, "seat", nextSeat);
    },
    handlePlanSeatInput(plan, event) {
      if (!plan) {
        return;
      }
      const input = event.target;
      const value = String(input.value || "").replace(/\D/g, "");
      if (!value) {
        input.value = "";
        return;
      }
      const seat = this.normalizeInputPlanSeat(value);
      input.value = String(seat);
      this.$set(plan, "seat", seat);
    },
    handlePlanSeatBlur(plan, event) {
      if (!plan) {
        return;
      }
      const seat = this.normalizeInputPlanSeat(event.target.value);
      event.target.value = String(seat);
      this.$set(plan, "seat", seat);
    },
    normalizeInputPlanSeat(value) {
      return Math.min(
        this.maxPlanSeat,
        Math.max(
          this.minPlanSeat,
          Math.floor(Number(value) || this.minPlanSeat),
        ),
      );
    },
    getPlanTotalPrice(plan) {
      return Number(plan.sellingPrice || 0) * this.getPlanSeat(plan);
    },
    getPlanTeamMonthlyIntegral(plan) {
      return Number(plan.monthlyIntegral || 0) * this.getPlanSeat(plan);
    },
    formatMonthlyPrice(sellingPrice) {
      const cycleMonths = {
        year: 12,
        halfYear: 6,
        season: 3,
        month: 1,
      };
      const value = fenToYuan(sellingPrice);
      if (Number.isNaN(value)) {
        return "0";
      }
      return formatPrice(value / (cycleMonths[this.activeCycle] || 1));
    },
    // 1积分单价取接口 integralUnitPrice（分/积分），转元展示
    formatPlanIntegralUnitPrice(plan) {
      return formatIntegralUnitPriceYuan(plan && plan.integralUnitPrice);
    },
    formatPrice,
    formatPriceFromFen,
    isFaqExpanded(id) {
      return this.expandedFaqIds.includes(id);
    },
    toggleFaq(id) {
      const index = this.expandedFaqIds.indexOf(id);
      if (index >= 0) {
        this.expandedFaqIds.splice(index, 1);
        return;
      }
      this.expandedFaqIds.push(id);
    },
    scrollToFaq() {
      const section = this.$refs.faqSection;
      if (!section) {
        return;
      }
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    },
  },
};
</script>

<style lang="less" scoped>
/* 尺寸按设计稿 px/14 转 rem；字号用 max(12PX, Nrem) 保证小屏不低于 12px */

.subscription-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  overflow: auto;
  background: rgba(0, 0, 0, 0.96);
  color: #f8fafc;
}

.subscription-controls {
  position: fixed;
  top: 1.71429rem;
  left: 2.28571rem;
  z-index: 3001;
  display: flex;
  gap: 0.71429rem;
}

.control-select {
  min-width: 9.42857rem;
  height: 2.42857rem;
  padding: 0 2rem 0 0.85714rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.57143rem;
  background: rgba(14, 16, 24, 0.96)
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M2.5 4.5 6 8l3.5-3.5'/%3E%3C/svg%3E")
    no-repeat right 0.71429rem center;
  color: #e5e7eb;
  font-size: ~"max(12PX, 0.92857rem)";
  cursor: pointer;
  appearance: none;
  outline: none;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover,
  &:focus {
    border-color: rgba(124, 58, 237, 0.65);
  }

  &:focus {
    box-shadow: 0 0 0 0.14286rem rgba(124, 58, 237, 0.18);
  }

  option {
    color: #111827;
    background: #ffffff;
  }
}

.subscription-dialog {
  position: relative;
  min-width: 100rem;
  min-height: 100vh;
  padding: 4.85714rem 2.85714rem 4rem;
  background: radial-gradient(
      circle at top center,
      rgba(109, 56, 255, 0.2),
      transparent 28%
    ),
    #06070b;
}

.subscription-close {
  position: fixed;
  top: 1.71429rem;
  right: 2.28571rem;
  z-index: 2;
  width: 2.42857rem;
  height: 2.42857rem;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  color: #9ca3af;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ~"max(12PX, 1rem)";
  line-height: 1;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
  }
}
.subscription-content {
  width: 100rem;
  margin: 0 auto;
  padding-top: 0.57143rem;
  text-align: center;
}

.activity-banner {
  box-sizing: border-box;
  width: 100%;
  margin: 0 0 2rem;
  padding: 1.28571rem 1.71429rem 1.42857rem;
  border: 1px solid rgba(92, 74, 49, 0.55);
  border-radius: 0.71429rem;
  background: radial-gradient(
      ellipse 42% 120% at 0% 50%,
      rgba(240, 185, 11, 0.14),
      transparent 68%
    ),
    linear-gradient(
      90deg,
      rgba(18, 14, 10, 0.98) 0%,
      rgba(12, 10, 18, 0.98) 52%,
      rgba(16, 10, 22, 0.98) 100%
    );
}

.activity-banner__tag {
  display: inline-flex;
  align-items: center;
  gap: 0.28571rem;
  margin: 0 0 0.57143rem;
  color: rgba(255, 255, 255, 0.45);
  font-size: ~"max(12PX, 0.92857rem)";
  line-height: 1.42857rem;
}

.activity-banner__arrow {
  color: rgba(255, 255, 255, 0.35);
  font-size: ~"max(12PX, 1rem)";
  line-height: 1;
}

.activity-banner__title {
  margin: 0 0 0.57143rem;
  color: #f0b90b;
  font-size: ~"max(12PX, 1.57143rem)";
  font-weight: 700;
  line-height: 2.28571rem;
}

.activity-banner__subtitle {
  margin: 0;
  color: rgba(255, 255, 255, 0.45);
  font-size: ~"max(12PX, 0.92857rem)";
  line-height: 1.42857rem;
}

.plan-header {
  position: relative;
}

.plan-title-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.57143rem;
}

/* 增席说明：保留背景、不要边框，文案与按钮同一行居中 */
.add-seats-banner {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: center;
  justify-content: center;
  gap: 0.85714rem;
  box-sizing: border-box;
  width: 100%;
  margin-top: 1.28571rem;
  padding: 0.85714rem 1.14286rem;
  border: none;
  border-radius: 0.85714rem;
  background: linear-gradient(
    90deg,
    rgba(124, 58, 237, 0.18) 0%,
    rgba(17, 18, 27, 0.86) 56%
  );
  text-align: left;
}

.add-seats-banner__copy {
  flex: 0 0 auto;
  margin: 0;
  color: rgba(255, 255, 255, 0.78);
  font-size: ~"max(12PX, 0.92857rem)";
  line-height: 1.57143rem;
  white-space: nowrap;
}

.add-seats-banner__btn {
  flex: 0 0 auto;
  min-width: 7.14286rem;
  height: 2.57143rem;
  padding: 0 1.14286rem;
  border: none;
  border-radius: 0.57143rem;
  background: #8b5cf6;
  color: #ffffff;
  font-size: ~"max(12PX, 1rem)";
  font-weight: 500;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.18s ease, box-shadow 0.18s ease;

  &:hover,
  &:focus-visible {
    background: #9d6ff8;
    box-shadow: 0 0.28571rem 1.14286rem rgba(139, 92, 246, 0.35);
  }
}

.add-seats-banner + .cycle-tabs {
  margin-top: 1.28571rem;
}

h1 {
  margin: 0;
  color: #ffffff;
  font-size: ~"max(12PX, 2rem)";
  line-height: 2.85714rem;
}

.faq-help-btn {
  position: relative;
  width: 1.42857rem;
  height: 1.42857rem;
  margin-top: 0.14286rem;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  flex-shrink: 0;

  &:hover,
  &:focus-visible {
    .faq-help-icon {
      border-color: #a855f7;
      color: #a855f7;
    }

    .faq-help-tooltip {
      opacity: 1;
      visibility: visible;
      transform: translate(-50%, 0);
    }
  }
}

.faq-help-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.28571rem;
  height: 1.28571rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.45);
  font-size: ~"max(12PX, 0.78571rem)";
  font-weight: 700;
  line-height: 1;
  transition: border-color 0.18s ease, color 0.18s ease;
}

.faq-help-tooltip {
  position: absolute;
  top: calc(100% + 0.57143rem);
  left: 50%;
  padding: 0.28571rem 0.71429rem;
  border-radius: 0.28571rem;
  background: #ffffff;
  color: #111827;
  font-size: ~"max(12PX, 0.85714rem)";
  font-weight: 600;
  line-height: 1.28571rem;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transform: translate(-50%, 0.28571rem);
  transition: opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease;
  pointer-events: none;
  z-index: 1;
}

.subtitle {
  margin-top: 0.42857rem;
  color: #6f778b;
  font-size: ~"max(12PX, 1rem)";
}

.cycle-tabs {
  margin-top: 1.85714rem;
  display: flex;
  justify-content: center;
  gap: 0.71429rem;

  button {
    min-width: 4.57143rem;
    height: 2.42857rem;
    border: 1px solid rgba(255, 255, 255, 0.08);
    border-radius: 1.21429rem;
    background: rgba(14, 16, 24, 0.9);
    color: #7d8496;
    cursor: pointer;
    font-size: ~"max(12PX, 0.92857rem)";
  }

  button.active {
    border-color: #7c3aed;
    background: rgba(124, 58, 237, 0.12);
    color: #a855f7;
  }
}

.plan-loading {
  min-height: 22.85714rem;
  margin-top: 2.28571rem;
}

.plan-empty {
  margin-top: 2.28571rem;
  padding: 3.42857rem 0;
  color: #8991a4;
  font-size: ~"max(12PX, 1rem)";
}

.plan-grid {
  margin-top: 2.28571rem;
  display: grid;
  gap: 1.14286rem;
}

.plan-card {
  position: relative;
  min-height: 27.42857rem;
  padding: 1.85714rem 1.42857rem 5.14286rem;
  border: 1px solid rgba(51, 56, 78, 0.85);
  border-radius: 0.57143rem;
  background: #11121b;
  text-align: left;
  cursor: pointer;
  box-shadow: 0 1.14286rem 2.71429rem rgba(0, 0, 0, 0.22);
  transition: border-color 0.18s ease, transform 0.18s ease, opacity 0.18s ease;

  &:hover {
    border-color: rgba(124, 58, 237, 0.88);
    transform: translateY(-0.14286rem);
  }

  &.active {
    border-color: #7c3aed;
    box-shadow: 0 0 0 1px rgba(124, 58, 237, 0.35),
      0 1.42857rem 3.42857rem rgba(0, 0, 0, 0.3);
  }

  &.disabled {
    cursor: not-allowed;
    opacity: 0.74;

    &:hover {
      border-color: rgba(51, 56, 78, 0.85);
      transform: none;
    }
  }

  h2 {
    margin: 0 0 1.28571rem;
    color: #f8fafc;
    font-size: ~"max(12PX, 1.28571rem)";
    line-height: 1.85714rem;
  }
}

.recommend-badge {
  position: absolute;
  top: 0;
  left: 50%;
  min-width: 3.85714rem;
  height: 1.71429rem;
  padding: 0 0.71429rem;
  border-radius: 0 0 0.28571rem 0.28571rem;
  background: linear-gradient(180deg, #8b5cf6, #6d28d9);
  color: #ffffff;
  font-size: ~"max(12PX, 0.85714rem)";
  font-weight: 700;
  line-height: 1.71429rem;
  text-align: center;
  transform: translateX(-50%);
}

.price-row {
  display: flex;
  align-items: center;
  gap: 0.28571rem;
}

.price {
  color: #ffc400;
  font-size: ~"max(12PX, 2rem)";
  font-weight: 800;
  line-height: 1;
  letter-spacing: 0;
}

.cycle-unit {
  color: #ffc400;
  font-size: ~"max(12PX, 0.85714rem)";
  // font-weight: 700;
}

.original-price {
  margin-left: 0.28571rem;
  color: #5d6476;
  font-size: ~"max(12PX, 0.92857rem)";
  text-decoration: line-through;
	margin-top: 0.28571rem;
}

.price-meta {
  margin-top: 0.28571rem;
  color: #8991a4;
  font-size: ~"max(12PX, 0.92857rem)";
}

.points-row {
  margin-top: 1.28571rem;
  display: flex;
  align-items: center;
  // gap: 0.57143rem;

  .points-value {
    color: #ffffff;
    font-size: ~"max(12PX, 2.42857rem)";
    font-weight: 900;
    line-height: 1;
  }

  span {
    color: #ffffff;
    font-size: ~"max(12PX, 1.07143rem)";
  }
}

.divider {
  height: 1px;
  margin: 1.28571rem 0;
  background: rgba(255, 255, 255, 0.06);
}

.benefit-list {
  min-height: 4.85714rem;
  margin: 0;
  padding: 0;
  list-style: none;

  li {
    margin-top: 0.57143rem;
    font-size: ~"max(11PX, 0.92857rem)";
    line-height: 1.28571rem;
  }
}

.vip {
  color: #ffc400;
  font-weight: 800;

  &::before {
    content: "★";
    margin-right: 0.28571rem;
  }
}

.discount {
  color: #00d29b;
  min-height: 1.57143rem;
  &::before {
    content: "✦";
    margin-right: 0.28571rem;
  }
}

.video {
  color: #6f778b;
  // padding-left: 1rem;
}

.open-button {
  width: 15.71429rem;
  height: 3.14286rem;
  margin-top: 1.14286rem;
  border: none;
  border-radius: 0.42857rem;
  background: #8b5cf6;
  color: #ffffff;
  cursor: pointer;
  font-size: ~"max(12PX, 1.07143rem)";
  font-weight: 800;
  transition: background 0.18s ease, box-shadow 0.18s ease;
  position: absolute;
  bottom: 0.71429rem;

  .plan-card:not(.disabled) &:hover {
    background: #9d6ff8;
    box-shadow: 0 0.28571rem 1.14286rem rgba(139, 92, 246, 0.35);
  }

  &.upgrade-plan-button {
    border-radius: 0.57143rem;
    background: #ffc300;
    color: #000000;
    box-shadow: none;

    .plan-card:not(.disabled) &:hover {
      background: #ffd01a;
      box-shadow: none;
    }
  }

  &.active-plan-button,
  &.active-plan-button:disabled {
    border: 1px solid #ffffff;
    background: transparent;
    color: #ffffff;
    cursor: not-allowed;
    box-shadow: none;
    opacity: 1;
  }

  &.contact-admin-plan-button,
  &.contact-admin-plan-button:disabled {
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(22, 24, 34, 0.9);
    color: #ffffff;
    cursor: not-allowed;
    box-shadow: none;
    opacity: 1;
  }

  &:disabled {
    border: 1px solid rgba(255, 255, 255, 0.06);
    background: rgba(22, 24, 34, 0.9);
    color: #596073;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.benefits-table {
  margin-top: 3.14286rem;

  h3 {
    margin: 0 0 1.28571rem;
    color: #ffffff;
    font-size: ~"max(12PX, 1.42857rem)";
    line-height: 2rem;
  }
}

.table-wrap {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 0.71429rem;
  background: #10111a;
  text-align: left;
}

.table-row {
  display: grid;
  min-height: 3rem;
  border-top: 1px solid rgba(255, 255, 255, 0.04);

  &:first-child {
    border-top: 0;
  }

  div {
    display: flex;
    align-items: center;
    padding: 0 1.28571rem;
    color: #d8deea;
    font-size: ~"max(12PX, 0.92857rem)";
    font-weight: 700;
  }

  div:first-child {
    color: #9aa3b5;
  }
}

.table-head {
  background: #191b2f;

  div {
    color: #ffffff;
  }
}

.faq-section {
  margin-top: 3.14286rem;

  h3 {
    margin: 0 0 1.28571rem;
    color: #ffffff;
    font-size: ~"max(12PX, 1.42857rem)";
    line-height: 2rem;
  }
}

.faq-list {
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.04);
  border-radius: 0.71429rem;
  background: #10111a;
  text-align: left;
}

.faq-item {
  border-top: 1px solid rgba(255, 255, 255, 0.04);

  &:first-child {
    border-top: 0;
  }
}

.faq-question {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.14286rem;
  width: 100%;
  min-height: 3.71429rem;
  padding: 1rem 1.28571rem;
  border: none;
  background: transparent;
  color: #f8fafc;
  cursor: pointer;
  font-size: ~"max(12PX, 1rem)";
  font-weight: 700;
  line-height: 1.57143rem;
  text-align: left;
  transition: background 0.18s ease;

  span {
    flex: 1;
  }

  i {
    flex-shrink: 0;
    color: #8b93a7;
    font-size: ~"max(12PX, 1rem)";
    font-weight: 700;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.02);
  }
}

.faq-answer {
  padding: 0 1.28571rem 1.14286rem;

  p {
    margin: 0 0 0.71429rem;
    color: #9aa3b5;
    font-size: ~"max(12PX, 0.92857rem)";
    font-weight: 400;
    line-height: 1.57143rem;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.enterprise-seat-block {
  text-align: left;
}

.seat-row {
  display: flex;
  align-items: center;
  gap: 0.57143rem;
  margin-bottom: 0.85714rem;
}

.seat-stepper {
  display: inline-flex;
  align-items: center;
  gap: 0.57143rem;
}

.seat-stepper__btn {
  width: 2.28571rem;
  height: 2.28571rem;
  border-radius: 0.57143rem;
  border: 1.5px solid #1e1e35;
  background: #15152a;
  color: #e8e6f0;
  font-size: ~"max(12PX, 1.14286rem)";
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  transition: 0.15s;

  &:hover:not(:disabled) {
    border-color: #7c3aed;
  }

  &:disabled {
    color: rgba(255, 255, 255, 0.3);
    cursor: not-allowed;
  }
}

.seat-stepper__value {
  min-width: 1.14286rem;
  width: 2.71429rem;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  color: #ffffff;
  font-size: ~"max(12PX, 1.14286rem)";
  font-weight: 700;
  line-height: 2rem;
  text-align: center;
  font-family: inherit;
}

.seat-label {
  color: rgba(255, 255, 255, 0.55);
  font-size: ~"max(12PX, 1rem)";
  line-height: 1.42857rem;
}

.seat-total-row {
  display: flex;
  align-items: baseline;
  gap: 0.42857rem;
  margin-bottom: 0.57143rem;
}

.seat-total-label,
.seat-total-unit {
  color: rgba(255, 255, 255, 0.45);
  font-size: ~"max(12PX, 0.92857rem)";
  line-height: 1.42857rem;
}

.seat-total-price {
  color: #ffc400;
  font-size: ~"max(12PX, 2rem)";
  font-weight: 700;
  line-height: 1.1;
}

.team-monthly-integral {
  color: #ffc400;
  font-size: ~"max(12PX, 0.85714rem)";
  line-height: 1.28571rem;
  margin-bottom: 0.85714rem;

  &-value {
    margin: 0 0.14286rem;
  }
}
</style>
